/**
 * Multi-dimensional verification (§12): locator / semantic-mapping / calculation
 * / claim are ORTHOGONAL axes and must never collapse into a single "verified"
 * badge. This derives the dimensions a `CitedValue` payload actually carries —
 * absent axes are simply not shown (we never fabricate a status).
 *
 *  - locator      — does the cited source actually hold this value?
 *                   (from `verification.status`; only a deterministic verifier
 *                    ever mints `verified`.)
 *  - semantic     — is that source the right concept for the requested metric?
 *                   (from `slot_context.status`; present only when checked.)
 *  - calculation  — was a derived value recomputed from its leaf inputs?
 *                   (present only for values with a derived source.)
 *  - claim        — is the underlying claim resolved / disputed / abstained?
 *                   (§13 resolution; only shown when the payload carries it.)
 */

import type { CitedValue, SourceRef } from "../../types/citationArtifact";

export type DimensionTone = "good" | "warn" | "bad" | "neutral";

export interface VerificationDimension {
	axis: "locator" | "semantic" | "calculation" | "claim";
	label: string;
	value: string;
	tone: DimensionTone;
	title: string;
}

function hasDerivedSource(sources: SourceRef[] | undefined): boolean {
	return (sources ?? []).some((s) => s?.kind === "derived");
}

function locatorDimension(value: CitedValue): VerificationDimension {
	const status = value.verification?.status ?? "unverified";
	const tone: DimensionTone =
		status === "verified"
			? "good"
			: status === "invalid"
				? "bad"
				: status === "not_applicable"
					? "neutral"
					: "warn";
	const label = status === "verified" ? "Verified" : "Locator";
	return {
		axis: "locator",
		label,
		value: status,
		tone,
		title:
			status === "verified"
				? "Locator verified: the cited source holds this exact value."
				: `Locator ${status}: not independently confirmed against the source.`,
	};
}

function semanticDimension(value: CitedValue): VerificationDimension | null {
	const ctx = value.slot_context;
	if (!ctx) return null;
	const tone: DimensionTone =
		ctx.status === "confirmed"
			? "good"
			: ctx.status === "contradicted"
				? "bad"
				: ctx.status === "mapped"
					? "warn"
					: "neutral";
	return {
		axis: "semantic",
		label: "Semantic",
		value: ctx.status,
		tone,
		title:
			ctx.status === "confirmed"
				? "Semantic match: the source row is the requested metric."
				: ctx.status === "mapped"
					? `Semantically mapped: source label "${ctx.source_row_label ?? "?"}" differs from the requested metric.`
					: ctx.status === "contradicted"
						? "Semantic conflict: the source period/label contradicts the request."
						: "Semantic mapping unconfirmed: the source label could not be checked.",
	};
}

function calculationDimension(value: CitedValue): VerificationDimension | null {
	if (!hasDerivedSource(value.sources)) return null;
	const recomputed = value.verification?.status === "verified";
	return {
		axis: "calculation",
		label: "Calculation",
		value: recomputed ? "recomputed" : "not_recomputed",
		tone: recomputed ? "good" : "warn",
		title: recomputed
			? "Calculation recomputed from its leaf inputs and matched."
			: "Derived value not independently recomputed.",
	};
}

/**
 * The claim/resolution axis is not part of the `CitedValue` contract yet; a
 * `resolution` field may ride on the payload once §13 observations are wired.
 * We read it defensively so the badge appears the moment it is populated,
 * without inventing a status when it is absent.
 */
function claimDimension(value: CitedValue): VerificationDimension | null {
	const resolution = (value as { resolution?: unknown }).resolution;
	if (typeof resolution !== "string") return null;
	const tone: DimensionTone =
		resolution === "resolved"
			? "good"
			: resolution === "disputed"
				? "bad"
				: "neutral";
	return {
		axis: "claim",
		label: "Claim",
		value: resolution,
		tone,
		title: `Claim ${resolution}.`,
	};
}

/** Every verification axis the payload carries, most-load-bearing first. */
export function deriveVerificationDimensions(
	value: CitedValue,
): VerificationDimension[] {
	return [
		locatorDimension(value),
		semanticDimension(value),
		calculationDimension(value),
		claimDimension(value),
	].filter((d): d is VerificationDimension => d !== null);
}

export const DIMENSION_TONE_CLASSES: Record<DimensionTone, string> = {
	good: "border-emerald-200 bg-emerald-50 text-emerald-700",
	warn: "border-amber-200 bg-amber-50 text-amber-700",
	bad: "border-red-200 bg-red-50 text-red-700",
	neutral: "border-gray-200 bg-gray-50 text-gray-600",
};
